import { useCallback, useEffect, useState } from 'react';
import { attendanceAPI, classesAPI, departmentsAPI } from '../services/api';
import { ClipboardCheck, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const emptyPolicyForm = {
  name: '',
  department_id: '',
  class_id: '',
  team_ids: [],
  school_start_time: '09:00',
  school_end_time: '18:00',
  lunch_start_time: '13:00',
  lunch_end_time: '14:00',
  break_start_time: '',
  break_end_time: '',
  attendance_mode: 'daily',
  late_grace_minutes: 10,
  half_day_minutes: 240,
  early_login_window_minutes: 120,
  early_logout_grace_minutes: 5,
  logout_grace_minutes: 30,
  logout_second_reminder_minutes: 15,
  flexible_work_hours: true,
  required_work_minutes: 480,
  is_active: true,
};

const attendanceModes = [
  {
    value: 'daily',
    label: 'Daily Office Attendance',
    shortLabel: 'Daily Office',
    description: 'Best for offices. Main gate or face IN marks present, late, or half-day using working hours.',
  },
  {
    value: 'timetable',
    label: 'Shift Attendance',
    shortLabel: 'Shift Based',
    description: 'Best for teams with assigned shifts. Reader scans are checked against shift timing and work area.',
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    shortLabel: 'Hybrid',
    description: 'Uses shift timing when a shift exists, then falls back to daily office timing when no shift matches.',
  },
];

const getAttendanceMode = (mode) => attendanceModes.find((item) => item.value === mode) || attendanceModes[0];
const formatTimeRange = (start, end) => (
  start && end ? `${String(start).slice(0, 5)} to ${String(end).slice(0, 5)}` : '-'
);

const AttendanceRules = () => {
  const { hasPermission } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyPolicyForm);

  const loadData = useCallback(async () => {
    const [policiesRes, departmentsRes, teamsRes] = await Promise.all([
      attendanceAPI.getPolicies(),
      departmentsAPI.getAll(),
      classesAPI.getAll(),
    ]);
    setPolicies(policiesRes.data.policies || []);
    setDepartments(departmentsRes.data.departments || []);
    setTeams(teamsRes.data.classes || []);
  }, []);

  useEffect(() => {
    loadData().catch(() => toast.error('Failed to load attendance rules'));
  }, [loadData]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyPolicyForm);
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      const payload = {
        ...form,
        department_id: form.department_id || null,
        class_id: form.team_ids[0] || null,
        team_ids: form.team_ids.map(Number),
      };

      if (editingId) {
        await attendanceAPI.updatePolicy(editingId, payload);
        toast.success('Attendance rule updated');
      } else {
        await attendanceAPI.createPolicy(payload);
        toast.success('Attendance rule created');
      }

      resetForm();
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save attendance rule');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (policy) => {
    setEditingId(policy.id);
    setForm({
      name: policy.name || '',
      department_id: policy.department_id || '',
      class_id: policy.class_id || '',
      team_ids: Array.isArray(policy.team_ids) ? policy.team_ids.map(Number) : (policy.class_id ? [Number(policy.class_id)] : []),
      school_start_time: policy.school_start_time?.slice(0, 5) || '',
      school_end_time: policy.school_end_time?.slice(0, 5) || '',
      lunch_start_time: policy.lunch_start_time?.slice(0, 5) || '',
      lunch_end_time: policy.lunch_end_time?.slice(0, 5) || '',
      break_start_time: policy.break_start_time?.slice(0, 5) || '',
      break_end_time: policy.break_end_time?.slice(0, 5) || '',
      attendance_mode: policy.attendance_mode || 'daily',
      late_grace_minutes: policy.late_grace_minutes || 0,
      half_day_minutes: policy.half_day_minutes || 0,
      early_login_window_minutes: policy.early_login_window_minutes ?? 120,
      early_logout_grace_minutes: policy.early_logout_grace_minutes ?? 5,
      logout_grace_minutes: policy.logout_grace_minutes || 30,
      logout_second_reminder_minutes: policy.logout_second_reminder_minutes || 15,
      flexible_work_hours: Boolean(policy.flexible_work_hours),
      required_work_minutes: policy.required_work_minutes || 480,
      is_active: policy.is_active,
    });
    setShowForm(true);
  };

  const handleDeactivate = async (policy) => {
    try {
      await attendanceAPI.updatePolicy(policy.id, {
        name: policy.name,
        department_id: policy.department_id,
        class_id: policy.class_id,
        team_ids: Array.isArray(policy.team_ids) ? policy.team_ids : [],
        school_start_time: policy.school_start_time,
        school_end_time: policy.school_end_time,
        lunch_start_time: policy.lunch_start_time,
        lunch_end_time: policy.lunch_end_time,
        break_start_time: policy.break_start_time,
        break_end_time: policy.break_end_time,
        attendance_mode: policy.attendance_mode || 'daily',
        late_grace_minutes: policy.late_grace_minutes,
        half_day_minutes: policy.half_day_minutes,
        early_login_window_minutes: policy.early_login_window_minutes,
        early_logout_grace_minutes: policy.early_logout_grace_minutes,
        logout_grace_minutes: policy.logout_grace_minutes,
        logout_second_reminder_minutes: policy.logout_second_reminder_minutes,
        flexible_work_hours: Boolean(policy.flexible_work_hours),
        required_work_minutes: policy.required_work_minutes || 480,
        is_active: false,
      });
      toast.success('Attendance rule deactivated');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate attendance rule');
    }
  };

  const canManage = hasPermission('attendance.manage');
  const selectedMode = getAttendanceMode(form.attendance_mode);
  const availableTeams = teams.filter((team) => {
    const assignedElsewhere = policies.some((policy) => (
      policy.id !== editingId
      && policy.is_active
      && Array.isArray(policy.team_ids)
      && policy.team_ids.map(Number).includes(Number(team.id))
    ));
    return !assignedElsewhere || form.team_ids.map(Number).includes(Number(team.id));
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Policies</h1>
          <p className="text-gray-500 text-sm mt-1">Configure office working hours, lunch, break timing, grace time, and shift fallback by department.</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowForm((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            {showForm ? 'Close Form' : 'Add Rule'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {attendanceModes.map((mode) => (
          <div key={mode.value} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <ClipboardCheck size={16} className={mode.value === 'daily' ? 'text-green-600' : mode.value === 'timetable' ? 'text-blue-600' : 'text-amber-600'} />
              <h2 className="text-sm font-bold text-gray-900">{mode.shortLabel}</h2>
            </div>
            <p className="mt-2 text-xs leading-5 text-gray-500">{mode.description}</p>
          </div>
        ))}
      </div>

      {showForm && canManage && (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Rule Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Default Attendance Policy"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
            <select
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Global rule</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>{department.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Teams using this shift</label>
            <select
              multiple
              value={form.team_ids.map(String)}
              onChange={(e) => setForm({
                ...form,
                team_ids: Array.from(e.target.selectedOptions).map((option) => Number(option.value)),
              })}
              className="min-h-[120px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableTeams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">Select one or more unassigned teams. Leave empty only for a department/global fallback rule.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Attendance Mode</label>
            <select
              value={form.attendance_mode}
              onChange={(e) => setForm({ ...form, attendance_mode: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {attendanceModes.map((mode) => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">{selectedMode.description}</p>
          </div>

          <label className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-gray-700 md:col-span-2">
            <input type="checkbox" checked={form.flexible_work_hours}
              onChange={(e) => setForm({ ...form, flexible_work_hours: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600" />
            <span><strong className="block text-gray-900">Flexible working hours</strong>
              Attendance is based on completed work time. IN is allowed four hours before shift start and OUT remains open four hours after shift end.</span>
          </label>

          {form.flexible_work_hours && <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Required Work Minutes</label>
            <input type="number" min="60" max="960" step="15" value={form.required_work_minutes}
              onChange={(e) => setForm({ ...form, required_work_minutes: Number(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            <p className="mt-1 text-xs text-gray-500">480 minutes equals 8 working hours.</p>
          </div>}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Office Start Time</label>
            <input
              type="time"
              value={form.school_start_time}
              onChange={(e) => setForm({ ...form, school_start_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Office End Time</label>
            <input
              type="time"
              value={form.school_end_time}
              onChange={(e) => setForm({ ...form, school_end_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Lunch Start Time</label>
            <input
              type="time"
              value={form.lunch_start_time}
              onChange={(e) => setForm({ ...form, lunch_start_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Lunch End Time</label>
            <input
              type="time"
              value={form.lunch_end_time}
              onChange={(e) => setForm({ ...form, lunch_end_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Break Start Time</label>
            <input
              type="time"
              value={form.break_start_time}
              onChange={(e) => setForm({ ...form, break_start_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Break End Time</label>
            <input
              type="time"
              value={form.break_end_time}
              onChange={(e) => setForm({ ...form, break_end_time: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Late Grace Minutes</label>
            <input
              type="number"
              min="0"
              value={form.late_grace_minutes}
              onChange={(e) => setForm({ ...form, late_grace_minutes: Number(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Half-Day Threshold Minutes</label>
            <input
              type="number"
              min="0"
              value={form.half_day_minutes}
              onChange={(e) => setForm({ ...form, half_day_minutes: Number(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Early Login Window (Minutes)</label>
            <input type="number" min="1" max="240" value={form.early_login_window_minutes}
              onChange={(e) => setForm({ ...form, early_login_window_minutes: Number(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="mt-1 text-xs text-gray-500">Accept an IN scan up to this many minutes before the scheduled start.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Early Logout Grace (Minutes)</label>
            <input type="number" min="0" max="240" value={form.early_logout_grace_minutes}
              onChange={(e) => setForm({ ...form, early_logout_grace_minutes: Number(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="mt-1 text-xs text-gray-500">An OUT scan earlier than this allowance is reported as early logout.</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Logout Grace Minutes</label>
            <input
              type="number" min="1" max="240"
              value={form.logout_grace_minutes}
              onChange={(e) => setForm({ ...form, logout_grace_minutes: Number(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Second Logout Reminder (Minutes)</label>
            <input
              type="number" min="1" max="239"
              value={form.logout_second_reminder_minutes}
              onChange={(e) => setForm({ ...form, logout_second_reminder_minutes: Number(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={form.is_active ? 'active' : 'inactive'}
              onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving...' : editingId ? 'Update Rule' : 'Create Rule'}
            </button>
            <button type="button" onClick={resetForm} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {policies.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No attendance rules found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Rule</th>
                <th className="px-4 py-3 text-left">Department</th>
                <th className="px-4 py-3 text-left">Team / Shift</th>
                <th className="px-4 py-3 text-left">Mode</th>
                <th className="px-4 py-3 text-left">Office Hours</th>
                <th className="px-4 py-3 text-left">Lunch</th>
                <th className="px-4 py-3 text-left">Break</th>
                <th className="px-4 py-3 text-left">Late Grace</th>
                <th className="px-4 py-3 text-left">Half-Day</th>
                <th className="px-4 py-3 text-left">Early Rules</th>
                <th className="px-4 py-3 text-left">Logout Window</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {policies.map((policy) => (
                <tr key={policy.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck size={16} className="text-blue-600" />
                      {policy.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{policy.department_name || 'Global'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {Array.isArray(policy.team_names) && policy.team_names.length > 0
                      ? policy.team_names.join(', ')
                      : 'Fallback (unassigned teams)'}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{getAttendanceMode(policy.attendance_mode).shortLabel}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatTimeRange(policy.school_start_time, policy.school_end_time)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatTimeRange(policy.lunch_start_time, policy.lunch_end_time)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatTimeRange(policy.break_start_time, policy.break_end_time)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{policy.late_grace_minutes} min</td>
                  <td className="px-4 py-3 text-gray-600">{policy.half_day_minutes} min</td>
                  <td className="px-4 py-3 text-gray-600">Login window {policy.early_login_window_minutes ?? 120} min · Logout grace {policy.early_logout_grace_minutes ?? 5} min</td>
                  <td className="px-4 py-3 text-gray-600">Reminder {policy.logout_second_reminder_minutes || 15} min · close {policy.logout_grace_minutes || 30} min</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${policy.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {policy.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {canManage ? (
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(policy)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50">
                          <Pencil size={12} />
                          Edit
                        </button>
                        {policy.is_active && (
                          <button onClick={() => handleDeactivate(policy)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                            <Trash2 size={12} />
                            Deactivate
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AttendanceRules;
